import React, { useRef, useState } from 'react';
import {
  ErreurImport, LigneRejetee, LignesImport,
  lireFichierImport, separerLignes, telechargerErreurs, telechargerValides,
} from '../utils/importExcel';

export interface ResultatImport {
  success: number;
  errors: ErreurImport[];
}

interface Options<R extends ResultatImport> {
  /** Envoie les lignes à l'API. Un rejet (Error) est affiché comme erreur globale. */
  envoyer: (lignes: Record<string, any>[]) => Promise<R>;
  /** Import réussi : la page rafraîchit sa liste et ferme la fenêtre. */
  onImported: () => void;
  onClose: () => void;
  messageVide?: string;
  maxLignes?: number;
}

const LOT_VIDE: LignesImport = { lignes: [], lignesExcel: [] };

/**
 * Logique commune des fenêtres d'import Excel (articles, tiers, familles, lignes).
 *
 * Import refusé à cause de lignes précises : les lignes sont séparées en « erreurs »
 * (cumulées d'un essai à l'autre, téléchargeables avec leur motif) et « valides »
 * (téléchargeables, ou importables directement — toujours tout ou rien sur ce lot).
 * Après l'import des valides, la fenêtre reste ouverte tant que des lignes en erreur
 * restent à télécharger ; le rafraîchissement de la page (onImported) se fait alors
 * à la fermeture.
 */
export function useImportExcel<R extends ResultatImport>({
  envoyer,
  onImported,
  onClose,
  messageVide = 'Le fichier ne contient aucune ligne valide.',
  maxLignes = 500,
}: Options<R>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [fichier, setFichier] = useState<{ nom: string; feuille: string; colonnes: string[] } | null>(null);
  const [lot, setLot] = useState<LignesImport>(LOT_VIDE);          // lignes du prochain / dernier envoi
  const [parseError, setParseError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<R | null>(null);
  const [lignesExcelResultat, setLignesExcelResultat] = useState<number[]>([]);   // n° Excel du lot de `result`
  const [rejetees, setRejetees] = useState<LigneRejetee[]>([]);
  const [valides, setValides] = useState<LignesImport | null>(null);
  const [validesImportees, setValidesImportees] = useState(false);   // fichier en cours
  const [rafraichirALaFermeture, setRafraichirALaFermeture] = useState(false);   // survit au changement de fichier

  const viderFichier = () => {
    setFichier(null);
    setLot(LOT_VIDE);
    setParseError(null);
    setResult(null);
    setLignesExcelResultat([]);
    setRejetees([]);
    setValides(null);
    setValidesImportees(false);
  };

  const fermer = () => {
    if (importing) return;   // Échap / clic extérieur pendant l'envoi : la réponse arriverait sur une fenêtre vidée
    viderFichier();
    if (rafraichirALaFermeture) {
      setRafraichirALaFermeture(false);
      onImported();
    }
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    viderFichier();
    const nom = file.name;
    setFichier({ nom, feuille: '', colonnes: [] });

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const lu = lireFichierImport(evt.target!.result as ArrayBuffer);
        setFichier({ nom, feuille: lu.feuille, colonnes: lu.colonnes });
        if (lu.lignes.length === 0) {
          setParseError(messageVide);
        } else if (lu.lignes.length > maxLignes) {
          setParseError(`Le fichier contient ${lu.lignes.length} lignes — maximum autorisé : ${maxLignes}.`);
        } else {
          setLot({ lignes: lu.lignes, lignesExcel: lu.lignesExcel });
        }
      } catch {
        setParseError('Impossible de lire le fichier. Vérifiez qu\'il s\'agit d\'un fichier Excel (.xlsx).');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const executer = async (cible: LignesImport) => {
    if (cible.lignes.length === 0) return;
    setImporting(true);
    try {
      let r: R;
      try {
        r = await envoyer(cible.lignes);
      } catch (err: any) {
        r = { success: 0, errors: [{ row: 0, message: err?.message || 'Erreur inconnue' }] } as R;
      }
      setLot(cible);
      setLignesExcelResultat(cible.lignesExcel);
      setResult(r);

      if (r.errors.length === 0) {
        if (rejetees.length === 0) {
          viderFichier();
          setRafraichirALaFermeture(false);
          onImported();
        } else {
          // Lignes valides importées : la fenêtre reste ouverte pour les lignes en erreur.
          setValides(null);
          setValidesImportees(true);
          setRafraichirALaFermeture(true);
        }
        return;
      }

      const decoupe = separerLignes(cible, r.errors);
      if (!decoupe) {
        setValides(null);
        return;
      }
      setRejetees(prev => [...prev, ...decoupe.rejetees].sort((a, b) => a.ligneExcel - b.ligneExcel));
      setValides(decoupe.valides.lignes.length > 0 ? decoupe.valides : null);
    } finally {
      setImporting(false);
    }
  };

  return {
    inputRef,
    choisirFichier: () => inputRef.current?.click(),
    handleFileChange,
    fileName: fichier?.nom ?? null,
    rows: lot.lignes,
    lignesExcel: lot.lignesExcel,
    parseError,
    importing,
    result,
    /** N° de ligne de l'API (`row`) → n° de ligne Excel du fichier. */
    ligneExcel: (row: number): number => lignesExcelResultat[row - 2] ?? row,
    rejetees,
    valides,
    /** true : les lignes valides viennent d'être importées, il ne reste que les lignes en erreur. */
    validesImportees,
    importer: () => executer(lot),
    importerValides: () => { if (valides) executer(valides); },
    telechargerErreurs: () => { if (fichier) telechargerErreurs(fichier.nom, fichier, rejetees); },
    telechargerValides: () => { if (fichier && valides) telechargerValides(fichier.nom, fichier, valides); },
    fermer,
  };
}
